import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Chart } from "react-google-charts";
import "../../styles/App.css";
import { getStudentDecision } from "../../api/dss.js";
import { criterionLabel } from "../../decision/criteria.js";
import { formatScore } from "../../decision/importColumns.mjs";

function statusLine(decision) {
  const currentLabel = decision.currentRoomLabel ?? decision.currentRoomId;
  const roomLabel = decision.roomLabel ?? decision.roomId;

  switch (decision.status) {
    case "retained":
      return `Залишаєтесь у кімнаті ${currentLabel ?? "—"}`;
    case "relocated":
      return `Переселення: ${currentLabel ?? "—"} → ${roomLabel ?? "—"}`;
    case "allocated":
      return `Поселення в кімнату ${roomLabel ?? "—"}`;
    case "unplaced":
      return "Місця не надано";
    default:
      return "Статус рішення невідомий";
  }
}

const StudentDecisionPage = () => {
  const { id } = useParams();
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchDecision = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const data = await getStudentDecision(id);
        setDecision(data);
      } catch (err) {
        console.error("Помилка завантаження пояснення рішення:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchDecision();
  }, [id]);

  if (loading) {
    return <p className="page-container">Завантаження...</p>;
  }

  if (notFound || !decision) {
    return (
      <p className="page-container">
        Пояснення рішення для цього студента ще не сформовано.
      </p>
    );
  }

  const breakdownEntries = Object.entries(decision.breakdown || {})
    .map(([code, share]) => ({
      code,
      label: criterionLabel(code),
      percent: share * 100,
    }))
    .sort((a, b) => b.percent - a.percent);

  const chartData = [
    ["Критерій", "Студент, %", "Ідеал, %"],
    ...breakdownEntries.map((entry) => [
      entry.label,
      Number(entry.percent.toFixed(1)),
      0,
    ]),
  ];

  const top2 = breakdownEntries.slice(0, 2);
  let explanationText = "";
  if (top2.length === 2) {
    explanationText = `Найбільше на відставання від ідеального рішення вплинули критерії «${
      top2[0].label
    }» (${top2[0].percent.toFixed(1)}%) та «${top2[1].label}» (${top2[1].percent.toFixed(
      1
    )}%).`;
  } else if (top2.length === 1) {
    explanationText = `Найбільше на відставання від ідеального рішення вплинув критерій «${
      top2[0].label
    }» (${top2[0].percent.toFixed(1)}%).`;
  }

  return (
    <div className="page-container">
      <h3 className="text-header">
        Пояснення рішення{decision.studentName ? `: ${decision.studentName}` : ""}
      </h3>

      <div className="input-component">
        <div className="form-container">
          <p className="input-block text-header">
            <strong>Місце в черзі:</strong> {decision.rank ?? "—"}
          </p>
          <p className="input-block text-header">
            <strong>Показник TOPSIS:</strong>{" "}
            {formatScore(decision.score)}
          </p>
          <p className="input-block text-header">
            <strong>Звітний період:</strong>{" "}
            {decision.periodStart && decision.periodEnd
              ? `${decision.periodStart} — ${decision.periodEnd}`
              : "—"}
          </p>
        </div>
      </div>

      <h3 className="text-header">{statusLine(decision)}</h3>

      {breakdownEntries.length > 0 && (
        <>
          <div className="chart-container">
            <h1 className="text-header">
              Частки критеріїв у відставанні від ідеалу
            </h1>
            <Chart
              chartType="Bar"
              width="100%"
              height={`${Math.max(200, breakdownEntries.length * 60)}px`}
              data={chartData}
              options={{
                title: "Частка критерію у відставанні (%)",
                legend: { position: "top" },
              }}
            />
          </div>

          <p className="text-header">{explanationText}</p>
        </>
      )}
    </div>
  );
};

export default StudentDecisionPage;
